import { useState, useEffect, useRef } from 'react';
import { Modal, ModalBody, Button, Stack } from '@7shifts/sous-chef-base';
import { useTranslation } from '@src/i18n';
import './InactivityModal.css';

const SIGN_OUT_COUNTDOWN_MS = 10_000;

interface InactivityModalProps {
    onStayActive: () => void;
    onSignOut: () => void;
}

export function InactivityModal({
    onStayActive,
    onSignOut
}: InactivityModalProps) {
    const [remainingMs, setRemainingMs] = useState(SIGN_OUT_COUNTDOWN_MS);
    const onSignOutRef = useRef(onSignOut);
    onSignOutRef.current = onSignOut;

    // Countdown starts immediately on mount — auto-signs out when it reaches 0.
    useEffect(() => {
        const start = Date.now();
        const id = setInterval(() => {
            const left = Math.max(
                0,
                SIGN_OUT_COUNTDOWN_MS - (Date.now() - start)
            );
            setRemainingMs(left);
            if (left <= 0) {
                clearInterval(id);
                onSignOutRef.current();
            }
        }, 250);
        return () => clearInterval(id);
    }, []);

    const countdownSecs = Math.ceil(remainingMs / 1000);
    const showCountdown = remainingMs <= SIGN_OUT_COUNTDOWN_MS / 2;
    const { t } = useTranslation();

    return (
        <Modal testId="inactivity-modal">
            <ModalBody>
                <Stack space={20}>
                    <p className="inactivity-modal__title">
                        {t('inactivity.title')}
                    </p>
                    <p className="inactivity-modal__subtitle">
                        {t('inactivity.subtitle')}
                    </p>
                    <div className="inactivity-modal__actions">
                        <Button
                            type="button"
                            theme="primary"
                            size="full-width"
                            onClick={onStayActive}
                            testId="inactivity-stay"
                        >
                            {t('inactivity.stayActive')}
                        </Button>
                        <Button
                            type="button"
                            theme="hollow"
                            size="full-width"
                            onClick={onSignOut}
                            testId="inactivity-sign-out"
                        >
                            {showCountdown
                                ? t('inactivity.signOutCountdown', {
                                      countdown: countdownSecs
                                  })
                                : t('inactivity.signOut')}
                        </Button>
                    </div>
                </Stack>
            </ModalBody>
        </Modal>
    );
}
